import { adminApi } from '../includes/contentManagementApi';

export default {
  /**
   * Run a dry-run migration: preview what would be migrated without persisting.
   * @param {string} tenantSlug - Optional tenant slug for single-tenant migration
   * @returns {Promise} Migration preview data (migrated = would migrate, skipped = would skip)
   */
  runMigrationDryRun(tenantSlug = null) {
    const params = { dryRun: 'true' };
    if (tenantSlug) {
      params.tenantSlug = tenantSlug;
    }
    return adminApi.post('/schemas/migrate-config', {}, { params });
  },

  /**
   * Run the actual migration (persists to DB).
   * @param {string} tenantSlug - Optional tenant slug for single-tenant migration
   * @returns {Promise} Migration results
   */
  runMigration(tenantSlug = null) {
    const params = {};
    if (tenantSlug) {
      params.tenantSlug = tenantSlug;
    }
    return adminApi.post('/schemas/migrate-config', {}, { params });
  },

  /**
   * Run current level migration dry run (preview only).
   * @param {string} tenantSlug - Optional tenant slug
   * @returns {Promise} Preview data (totalUsers, migrated, skipped, errors)
   */
  runCurrentLevelDryRun(tenantSlug = null) {
    const params = { dryRun: 'true' };
    if (tenantSlug) {
      params.tenantSlug = tenantSlug;
    }
    return adminApi.post('/schemas/migrate-current-level', {}, { params });
  },

  /**
   * Run current level migration (persists to DB).
   * @param {string} tenantSlug - Optional tenant slug
   * @returns {Promise} Migration results
   */
  runCurrentLevelMigration(tenantSlug = null) {
    const params = {};
    if (tenantSlug) {
      params.tenantSlug = tenantSlug;
    }
    return adminApi.post('/schemas/migrate-current-level', {}, { params });
  },

  /**
   * Run dev tip dimension rotation migration dry run (preview only).
   * @param {string} tenantSlug - Optional tenant slug
   * @returns {Promise} Preview data (totalUsersMissing, usersMigrated, rotationEntriesAdded, errors)
   */
  runDevTipDimensionRotationDryRun(tenantSlug = null) {
    const params = { dryRun: 'true' };
    if (tenantSlug) {
      params.tenantSlug = tenantSlug;
    }
    return adminApi.post('/schemas/migrate-dev-tip-dimension-rotation', {}, { params });
  },

  /**
   * Run dev tip dimension rotation migration (persists to DB).
   * @param {string} tenantSlug - Optional tenant slug
   * @returns {Promise} Migration results
   */
  runDevTipDimensionRotationMigration(tenantSlug = null) {
    const params = {};
    if (tenantSlug) {
      params.tenantSlug = tenantSlug;
    }
    return adminApi.post('/schemas/migrate-dev-tip-dimension-rotation', {}, { params });
  },

  /**
   * Dry-run: convert legacy UTC schedule numbers → Europe/Berlin wall-clock (schemas).
   */
  runScheduleWallClockDryRun(tenantSlug = null) {
    const params = { dryRun: 'true' };
    if (tenantSlug) {
      params.tenantSlug = tenantSlug;
    }
    return adminApi.post('/schemas/migrate-schedules-utc-to-wall-clock', {}, { params });
  },

  /**
   * Apply: convert legacy UTC schedule numbers → Europe/Berlin wall-clock (schemas).
   * Requires confirm=true (run once per tenant).
   */
  runScheduleWallClockMigration(tenantSlug = null) {
    const params = { confirm: 'true' };
    if (tenantSlug) {
      params.tenantSlug = tenantSlug;
    }
    return adminApi.post('/schemas/migrate-schedules-utc-to-wall-clock', {}, { params });
  },

  /**
   * Dry-run: KSS series repeatRules start/end UTC → Europe/Berlin wall-clock.
   */
  runKssScheduleTimesDryRun() {
    return adminApi.post(
      '/assessments/kss/admin/migrate-schedule-times',
      {},
      { params: { dryRun: 'true' } },
    );
  },

  /**
   * Apply: KSS series schedule times + rematerialize scheduled rows (confirm=true).
   */
  runKssScheduleTimesMigration() {
    return adminApi.post(
      '/assessments/kss/admin/migrate-schedule-times',
      {},
      { params: { confirm: 'true' } },
    );
  },

  /**
   * One-time post-release cleanup: legacy status_new / date_notNew → viewed / viewedAt.
   * Listed with other schema migrations (Developer → Migrations).
   */
  runDevTipViewedFieldsMigrationDryRun(tenantSlug = null) {
    const params = { dryRun: 'true' };
    if (tenantSlug) {
      params.tenantSlug = tenantSlug;
    }
    return adminApi.post('/schemas/migrate-dev-tip-viewed-fields', {}, { params });
  },

  runDevTipViewedFieldsMigration(tenantSlug = null) {
    const params = {};
    if (tenantSlug) {
      params.tenantSlug = tenantSlug;
    }
    return adminApi.post('/schemas/migrate-dev-tip-viewed-fields', {}, { params });
  },

  /**
   * Tenant roles: dry-run from legacy config (admin_emails, company_superuser lists → User.roles).
   */
  runTenantRolesDryRun() {
    return adminApi.post('/tenant-roles/migrate-from-config', { dryRun: true });
  },

  /**
   * Tenant roles: apply migration from legacy config.
   */
  runTenantRolesMigration() {
    return adminApi.post('/tenant-roles/migrate-from-config', { dryRun: false });
  },

  /**
   * TEST ONLY: Preview dev tip schedule (parallel/alternate) - next up to 40 occurrences.
   * Optional userId: when provided, dimensions are shown from that user's devTipDimensionRotation.
   */
  getDevTipSchedulePreview(userId) {
    const params = userId ? { userId } : {};
    return adminApi.get('/scheduler/dev-tip-schedule-preview', { params });
  },

  /** Tenant `features` cleanup (normalize rows, strip legacy keys, dedupe legacy type — preview). */
  runFeaturesMigrateDryRun() {
    return adminApi.post('/features/migrate', {}, { params: { dryRun: 'true' } });
  },

  /** Apply tenant `features` cleanup migration for the current tenant. */
  runFeaturesMigrate() {
    return adminApi.post('/features/migrate', {}, { params: { dryRun: 'false' } });
  },

  /**
   * [HOTFIX] 360mindset dimension correction dry run (maximator / app_maximator only).
   */
  run360mindsetDimensionCorrectionDryRun() {
    return adminApi.post('/schemas/migrate-360mindset-dimension-correction', {}, { params: { dryRun: 'true' } });
  },

  /**
   * [HOTFIX] Apply 360mindset dimension correction (maximator / app_maximator only).
   */
  run360mindsetDimensionCorrection() {
    return adminApi.post('/schemas/migrate-360mindset-dimension-correction', {}, { params: {} });
  },

  /** Empty dimension repair dry run (active CMS tenant). */
  runEmptyDimensionRepairDryRun() {
    return adminApi.post('/schemas/migrate-empty-dimension-repair', {}, { params: { dryRun: 'true' } });
  },

  /** Apply empty dimension repair for one assessment (active CMS tenant). */
  runEmptyDimensionRepairOne(assessmentId) {
    return adminApi.post('/schemas/migrate-empty-dimension-repair', {}, { params: { assessmentId } });
  },

  /** Apply empty dimension repair for all flagged assessments (active CMS tenant). */
  runEmptyDimensionRepair() {
    return adminApi.post('/schemas/migrate-empty-dimension-repair', {}, { params: {} });
  },

  /**
   * Phase 1: backfill kss_series config docs for the active tenant (dry-run preview).
   * Roster uses Option B; historical-only users are excluded.
   */
  runKssSeriesConfigMigrationDryRun() {
    return adminApi.post(
      '/assessments/kss/admin/migrate-series-config',
      {},
      { params: { dryRun: 'true' } },
    );
  },

  /**
   * Phase 1: apply kss_series config backfill for the active tenant.
   */
  runKssSeriesConfigMigration() {
    return adminApi.post(
      '/assessments/kss/admin/migrate-series-config',
      {},
      { params: { dryRun: 'false' } },
    );
  },

  /**
   * Dry-run: legacy User.externalEvaluationEnabled → enabledExternalEvaluationSchemas (SoT).
   * Users collection only; assessments untouched.
   */
  runExternalEvaluationEnabledToSchemasDryRun(tenantSlug = null) {
    const params = { dryRun: 'true' };
    if (tenantSlug) {
      params.tenantSlug = tenantSlug;
    }
    return adminApi.post('/schemas/migrate-external-evaluation-enabled-to-schemas', {}, { params });
  },

  /**
   * Apply: legacy EE boolean → SoT schema grants (current tenant).
   */
  runExternalEvaluationEnabledToSchemasMigration(tenantSlug = null) {
    const params = {};
    if (tenantSlug) {
      params.tenantSlug = tenantSlug;
    }
    return adminApi.post('/schemas/migrate-external-evaluation-enabled-to-schemas', {}, { params });
  },
};
